import React, { useEffect } from 'react'
import {
  Table, Button, Card, Form, Input, Select, Switch, message, Drawer, Space, Tooltip, Popconfirm, Col,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, BankOutlined, CheckCircleOutlined, SearchOutlined, StarFilled,
} from '@ant-design/icons'
import { useMasterData, type BankAccount } from '@/context/MasterDataContext'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { generateCode } from '@/utils/codeGenerator'
import { ApiError } from '@/services/api'
import './MasterForm.css'

const BASE = '/master/bank-accounts'
const MONO: React.CSSProperties = { fontFamily: 'JetBrains Mono, monospace' }

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
    <div style={{ width: 140, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0B1426', flex: 1 }}>{value || <span style={{ color: '#ccc' }}>—</span>}</div>
  </div>
)

const BankMaster: React.FC = () => {
  const { bankAccounts, deleteBankAccount, saveBankAccount, masterDataLoaded } = useMasterData()
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)
  const [viewRecord, setViewRecord] = React.useState<BankAccount | null>(null)
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = React.useState(false)

  /* List search / filter */
  const [searchText, setSearchText] = React.useState('')
  const [filterStatus, setFilterStatus] = React.useState('')

  const activeCount = bankAccounts.filter(b => b.status === 'active').length
  const defaultAccount = bankAccounts.find(b => b.isDefault && b.status === 'active')

  const filteredAccounts = bankAccounts.filter(b => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      b.bankName.toLowerCase().includes(q) ||
      (b.branchName || '').toLowerCase().includes(q) ||
      b.accountNumber.toLowerCase().includes(q)
    const matchStatus = !filterStatus || b.status === filterStatus
    return matchSearch && matchStatus
  })

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
    } else if (mode === 'edit') {
      const rec = bankAccounts.find(b => b.id === editId)
      if (rec) form.setFieldsValue(rec)
      else goList()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editId, bankAccounts])

  const handleDelete = async (id: string) => {
    try {
      await deleteBankAccount(id)
      message.success('Bank account removed')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  const onFinish = async (values: any) => {
    const isEdit = mode === 'edit'
    let id = editId
    if (!isEdit) {
      try {
        id = await generateCode('bank_masters')
      } catch (err) {
        message.error(err instanceof ApiError ? err.message : "Couldn't generate a bank account code — check your connection and try again.")
        return
      }
    }
    const payload = { ...values, id, isDefault: !!values.isDefault }
    setSubmitting(true)
    try {
      await saveBankAccount(payload, isEdit)
      message.success(isEdit ? 'Bank account updated' : 'Bank account added')
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  const columns = [
    {
      title: 'Code', dataIndex: 'id', width: 100,
      render: (v: string) => <span style={{ ...MONO, fontWeight: 700, color: '#1677ff', fontSize: 13 }}>{v}</span>,
    },
    {
      title: 'Bank Name', dataIndex: 'bankName',
      render: (v: string, r: BankAccount) => (
        <span style={{ fontWeight: 600, color: '#1677ff', display: 'flex', alignItems: 'center', gap: 6 }}>
          {v}
          {r.isDefault && <Tooltip title="Default account shown on invoices"><StarFilled style={{ color: '#F59E0B', fontSize: 12 }} /></Tooltip>}
        </span>
      ),
    },
    {
      title: 'Branch', dataIndex: 'branchName',
      render: (v: string) => <span style={{ color: '#4A5568', fontSize: 13 }}>{v || '—'}</span>,
    },
    {
      title: 'Account No', dataIndex: 'accountNumber',
      render: (v: string) => <span style={{ ...MONO, fontSize: 13 }}>{v}</span>,
    },
    {
      title: 'IFSC Code', dataIndex: 'ifscCode',
      render: (v: string) => <span style={{ ...MONO, fontSize: 13 }}>{v || '—'}</span>,
    },
    {
      title: 'SWIFT Code', dataIndex: 'swiftCode',
      render: (v: string) => <span style={{ ...MONO, fontSize: 13 }}>{v || '—'}</span>,
    },
    {
      title: 'Status', dataIndex: 'status', width: 110,
      render: (s: string) => (
        <span className={`pill ${s === 'active' ? 'pill-green' : 'pill-neutral'}`}>
          {s === 'active' ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: any, record: BankAccount) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="View">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setViewRecord(record)} />
          </Tooltip>
          <Tooltip title="Edit">
            <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
          </Tooltip>
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="link" danger size="small" icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // ── Full-page Add / Edit form ──────────────────────────────────
  if (isForm) {
    return (
      <MasterFormShell
        title={mode === 'edit' ? 'Edit Bank Account' : 'Add Bank Account'}
        subtitle="Company bank account — shown on invoices"
        onBack={goList}
        extra={mode === 'edit' && editId ? (
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form form={form} layout="vertical" onFinish={onFinish} style={{ maxWidth: 560 }}>
          <Form.Item name="bankName" label="Bank Name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. HDFC Bank Ltd" />
          </Form.Item>
          <Form.Item name="branchName" label="Branch" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. Chitlapakkam" />
          </Form.Item>
          <Form.Item name="accountHolderName" label="Account Holder Name">
            <Input placeholder="e.g. Shakthi Power Pvt Ltd" />
          </Form.Item>
          <Form.Item name="accountNumber" label="Account Number" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. 50200020013815" style={MONO} />
          </Form.Item>
          <Form.Item
            name="ifscCode" label="IFSC Code"
            rules={[{ pattern: /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/, message: 'Enter a valid 11-character IFSC code (e.g. HDFC0000260)' }]}
            normalize={(v: string) => (v || '').toUpperCase()}
          >
            <Input placeholder="e.g. HDFC0000260" style={MONO} />
          </Form.Item>
          <Form.Item
            name="swiftCode" label="SWIFT Code"
            rules={[{ pattern: /^[A-Za-z]{6}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?$/, message: 'Enter a valid 8 or 11-character SWIFT/BIC code' }]}
            normalize={(v: string) => (v || '').toUpperCase()}
          >
            <Input placeholder="e.g. HDFCINBB" style={MONO} />
          </Form.Item>
          <Form.Item name="isDefault" label="Default for Invoices" valuePropName="checked" initialValue={false}
            tooltip="Shown on the invoice PDF's Bank Details block. Only one account should be marked default.">
            <Switch />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="active">
            <Select options={[
              { value: 'active',   label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]} />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>{mode === 'edit' ? 'Update Bank Account' : 'Add Bank Account'}</Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={7} />

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BankOutlined style={{ color: '#001A4D' }} /> Bank Master
          </h2>
          <p className="page-subtitle">Company bank accounts — the default one is shown on the Invoice PDF's Bank Details block</p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>Add Bank Account</Button>
      </div>

      <KpiCardRow
        cards={[
          { label: 'Total Accounts', value: bankAccounts.length, icon: <BankOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
          { label: 'Default (used on invoices)', value: defaultAccount ? defaultAccount.bankName : 'None set', icon: <StarFilled style={{ fontSize: 24, color: '#F59E0B' }} />, iconBg: 'rgba(245,158,11,0.08)', borderColor: '#F59E0B' },
        ]}
      />

      {/* ── Filter bar ── */}
      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by bank, branch or account no..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredAccounts}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} records` }}
        />
      </Card>

      {/* ── Detail Drawer ── */}
      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{viewRecord?.bankName}</div>
            <div style={{ fontSize: 12, color: '#8A93A6', ...MONO }}>{viewRecord?.id}</div>
          </div>
        }
        open={!!viewRecord}
        onClose={() => setViewRecord(null)}
        width={380}
        extra={
          <Space>
            <Tooltip title="Edit">
              <Button type="text" icon={<EditOutlined />} onClick={() => { if (viewRecord) goEdit(viewRecord.id); setViewRecord(null) }} />
            </Tooltip>
            <Tooltip title="Delete">
              <Popconfirm
                title="Delete this record?"
                description="This action cannot be undone."
                onConfirm={() => { handleDelete(viewRecord!.id); setViewRecord(null) }}
                okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </Tooltip>
          </Space>
        }
      >
        {viewRecord && (
          <div>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
              <span className={`pill ${viewRecord.status === 'active' ? 'pill-green' : 'pill-neutral'}`}>
                {viewRecord.status === 'active' ? 'Active' : 'Inactive'}
              </span>
              {viewRecord.isDefault && <span className="pill" style={{ background: '#F59E0B18', color: '#D97706' }}>Default for Invoices</span>}
            </div>
            <DetailRow label="Code"               value={<span style={MONO}>{viewRecord.id}</span>} />
            <DetailRow label="Bank Name"           value={<strong>{viewRecord.bankName}</strong>} />
            <DetailRow label="Branch"              value={viewRecord.branchName} />
            <DetailRow label="Account Holder"      value={viewRecord.accountHolderName} />
            <DetailRow label="Account Number"      value={<span style={MONO}>{viewRecord.accountNumber}</span>} />
            <DetailRow label="IFSC Code"           value={<span style={MONO}>{viewRecord.ifscCode}</span>} />
            <DetailRow label="SWIFT Code"          value={<span style={MONO}>{viewRecord.swiftCode}</span>} />
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default BankMaster
